"use client";

/**
 * useVisualViewportHeight — tracks `window.visualViewport` so a pinned mobile
 * shell can follow the on-screen keyboard and iOS chrome instead of being
 * pushed behind them (which `100dvh` does, since it ignores the keyboard).
 *
 * ONE store per document. Every consumer (portal shell, staff shell, compose
 * surfaces, bottom sheets) subscribes to the same module-level store, so the
 * three CSS variables have exactly one writer and one `visualViewport`
 * listener pair, regardless of how many components mount:
 * - `--app-visual-viewport-height`
 * - `--app-visual-viewport-offset-top`
 * - `--app-viewport-bottom-gap` (layout bottom minus visual bottom; for fixed
 *   FABs/nav `bottom:` — not for in-flow content insets)
 *
 * The variables are written synchronously inside the event handler (no rAF),
 * so a `top: var(--app-visual-viewport-offset-top)` shell never trails the
 * viewport by a frame. React consumers re-render only when a value changed.
 *
 * Listens to both `resize` and `scroll` because iOS fires `scroll` (not
 * `resize`) while the address bar collapses/expands.
 *
 * Pinch-zoom (`scale !== 1`) freezes the last stable metrics — see
 * visualViewportShellMetrics.
 *
 * Returns `null` on the server and before the first measurement — callers fall
 * back to `100dvh` / the CSS variable defaults.
 */

import { useSyncExternalStore } from "react";
import {
  type VisualViewportShellMetrics,
  isVisualViewportZoomed,
  visualViewportShellMetrics,
} from "./visual-viewport-shell-metrics";

export interface VisualViewportMetrics {
  readonly height: number;
  readonly offsetTop: number;
}

interface VisualViewportStore {
  snapshot: VisualViewportMetrics | null;
  stable: VisualViewportShellMetrics | null;
  readonly listeners: Set<() => void>;
  detach: (() => void) | null;
}

const store: VisualViewportStore = {
  snapshot: null,
  stable: null,
  listeners: new Set(),
  detach: null,
};

function writeCssVar(name: string, value: string): void {
  const style = document.documentElement.style;
  if (style.getPropertyValue(name) !== value) style.setProperty(name, value);
}

/** Layout-viewport height: stable under pinch-zoom on WebKit, unlike innerHeight. */
function readLayoutHeight(): number {
  const clientHeight: number = document.documentElement.clientHeight;
  return clientHeight > 0 ? clientHeight : window.innerHeight;
}

function measure(): void {
  const vv = window.visualViewport;
  if (vv == null) return;
  const metrics = visualViewportShellMetrics(
    vv,
    readLayoutHeight(),
    store.stable,
  );
  if (!isVisualViewportZoomed(vv.scale)) store.stable = metrics;

  writeCssVar("--app-visual-viewport-height", `${metrics.height}px`);
  writeCssVar("--app-visual-viewport-offset-top", `${metrics.offsetTop}px`);
  writeCssVar("--app-viewport-bottom-gap", `${metrics.bottomGap}px`);

  const previous = store.snapshot;
  if (
    previous != null &&
    previous.height === metrics.height &&
    previous.offsetTop === metrics.offsetTop
  ) {
    return;
  }
  store.snapshot = { height: metrics.height, offsetTop: metrics.offsetTop };
  for (const listener of store.listeners) listener();
}

function attach(): void {
  if (store.detach != null) return;
  const vv = window.visualViewport;
  if (vv == null) return;
  const onChange = (): void => {
    measure();
  };
  vv.addEventListener("resize", onChange);
  vv.addEventListener("scroll", onChange);
  store.detach = (): void => {
    vv.removeEventListener("resize", onChange);
    vv.removeEventListener("scroll", onChange);
    store.detach = null;
  };
  measure();
}

function subscribe(listener: () => void): () => void {
  store.listeners.add(listener);
  attach();
  return (): void => {
    store.listeners.delete(listener);
    // Intentionally NOT clearing the CSS vars on the last unsubscribe: another
    // consumer may mount a moment later; desktop layouts ignore them anyway.
    if (store.listeners.size === 0) store.detach?.();
  };
}

/**
 * Keep the CSS variables in sync without any React state (for shells that
 * only need the vars). Returns the unsubscribe function.
 */
export function subscribeVisualViewportCssVars(): () => void {
  return subscribe((): void => undefined);
}

/** Last measured metrics, or `null` before the first measurement. */
export function readVisualViewportSnapshot(): VisualViewportMetrics | null {
  return store.snapshot;
}

function getSnapshot(): VisualViewportMetrics | null {
  return store.snapshot;
}

function getServerSnapshot(): VisualViewportMetrics | null {
  return null;
}

export function useVisualViewportHeight(): VisualViewportMetrics | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
